import { usePrivy } from "@privy-io/react-auth";
import { useQuery } from "@tanstack/react-query";
import type { MyQuestsResponse } from "@/components/QuestCard";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

/**
 * Fetches the authenticated user's quest progress from /api/rewards/my-quests.
 *
 * Returns the full MyQuestsResponse:
 * quests[], pointsSummary, filterCounts, appliedFilters.
 * Shared across QuestsPage, HomePage, and RewardsCard so every
 * consumer shares the same cache entry.
 */
export const useMyQuests = (options?: { enabled?: boolean }) => {
	const { authenticated, ready, getAccessToken } = usePrivy();

	return useQuery({
		queryKey: [QueryKey.MY_QUESTS],
		queryFn: async (): Promise<MyQuestsResponse> => {
			const token = await getAccessToken();
			if (!token) throw new Error("Unable to retrieve access token");

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/my-quests`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (!response.ok) {
				throw new Error(`Failed to fetch quests (${response.status})`);
			}

			return (await response.json()) as MyQuestsResponse;
		},
		enabled: (options?.enabled ?? true) && ready && authenticated,
		refetchOnWindowFocus: true,
	});
};
